import React, { useMemo, useState } from 'react';
import { BriefcaseBusiness, CircleX, CheckCircle2, ListChecks } from 'lucide-react';
import { profileIconMap } from '../utils/profileIcons';
import { formatMonthValue, parseAppDate } from '../utils/date';

function SidebarTaskSummary({ tasks = [] }) {
  const [filterMode, setFilterMode] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => formatMonthValue());

  const filteredTasks = useMemo(() => {
    if (filterMode !== 'month' || !selectedMonth) {
      return tasks;
    }

    return tasks.filter((task) => {
      const date = parseAppDate(task.created_at);
      if (!date) return false;
      const taskMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return taskMonth === selectedMonth;
    });
  }, [filterMode, selectedMonth, tasks]);

  const stats = useMemo(() => {
    const total = filteredTasks.length;
    const active = filteredTasks.filter((task) => ['in_progress', 'on_hold', 'in_review'].includes(task.status)).length;
    const cancelled = filteredTasks.filter((task) => task.status === 'cancelled').length;
    const done = filteredTasks.filter((task) => task.status === 'done').length;

    return [
      { key: 'total', label: 'Đã giao', value: total, icon: BriefcaseBusiness },
      { key: 'active', label: 'Đang làm', value: active, icon: ListChecks },
      { key: 'cancelled', label: 'Đã huỷ', value: cancelled, icon: CircleX },
      { key: 'done', label: 'Đã xong', value: done, icon: CheckCircle2 },
    ];
  }, [filteredTasks]);

  return (
    <section className="sidebar-task-summary">
      <div className="sidebar-task-summary-head">
        <div>
          <div className="sidebar-section-title">Tổng số lượng task</div>
          <div className="sidebar-section-hint">Thống kê theo phạm vi task bạn đang nhìn thấy trên bảng.</div>
        </div>
      </div>

      <div className="sidebar-task-summary-filters">
        <select
          className="form-input"
          value={filterMode}
          onChange={(e) => {
            const value = e.target.value;
            setFilterMode(value);
            if (value === 'month' && !selectedMonth) {
              setSelectedMonth(formatMonthValue());
            }
          }}
        >
          <option value="all">Toàn thời gian</option>
          <option value="month">Theo tháng</option>
        </select>

        {filterMode === 'month' && (
          <input
            type="month"
            className="form-input"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        )}
      </div>

      <div className="sidebar-task-stats-grid">
        {stats.map((item) => {
          const Icon = item.icon;
          return (
            <div className="sidebar-task-stat-card" key={item.key}>
              <div className="sidebar-task-stat-top">
                <span className="sidebar-task-stat-label">{item.label}</span>
                <span className="sidebar-task-stat-icon"><Icon size={15} /></span>
              </div>
              <div className="sidebar-task-stat-value">{item.value}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Sidebar({ open, profile, tasks = [] }) {
  const safeProfile = profile || {};
  const items = Array.isArray(safeProfile.items) ? safeProfile.items : [];

  return (
    <aside className={`sidebar ${open ? '' : 'collapsed'}`}>
      <div className="sidebar-profile">
        <div className="sidebar-avatar">
          {safeProfile.avatar_url ? (
            <img src={safeProfile.avatar_url} alt={safeProfile.name || 'Profile'} />
          ) : (
            <div className="avatar-placeholder">TL</div>
          )}
        </div>
        <div className="sidebar-name">{safeProfile.name || 'Le Anh Tien'}</div>
        <div className="sidebar-username">{safeProfile.username || 'leanhtiennn'}</div>
        <div className="sidebar-bio">{safeProfile.bio || ''}</div>
      </div>

      <div className="sidebar-info">
        {items.map((item) => {
          const Icon = profileIconMap[item.icon] || profileIconMap['circle-help'];
          const content = item.url ? (
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              {item.value}
            </a>
          ) : (
            <span>{item.value}</span>
          );

          return (
            <div className="sidebar-info-item" key={item.id || `${item.label}-${item.value}`} title={item.label}>
              <Icon size={15} />
              {content}
            </div>
          );
        })}

        <SidebarTaskSummary tasks={tasks} />
      </div>
    </aside>
  );
}
